import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.chat');
import { OnInit } from "@angular/core";
import { Service } from "src/libs/portal/season/service";
import { Markdown } from "../../libs/markdown.ts";
import showdown from "showdown";

type ChatMessage = {
    text?: string;
    section1?: string;
    section2?: string;
    isMine: boolean;
    isLoading?: boolean;
    isSearching?: boolean;
    showContinueButton?: boolean;
    files?: File[];

    isLoadingSection1?: boolean;
    isLoadingSection2?: boolean;
};


@Component({
    selector: 'wiz-component-chat',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/component.chat/view.scss */
@keyframes fadeSlide {
  0% {
    background-position: 0% 50%;
    transform: scale(1);
  }
  50% {
    background-position: 100% 50%;
    transform: scale(1.05);
  }
  100% {
    background-position: 0% 50%;
    transform: scale(1);
  }
}
.search-animation {
  font-weight: bold;
  font-size: 14px;
  background: linear-gradient(to right, #4a4a4a, #888888, #4a4a4a);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  animation: fadeSlide 1.5s infinite linear;
}`],
})
export class ComponentChatComponent implements OnInit {
    constructor(@Inject( Service) public service: Service) { }

    public message: string = "";
    public messages: ChatMessage[] = [];

    public sessionId: string = "";
    public isStreaming: boolean = false;
    public isAutoScrollEnabled: boolean = true;
    public currentFormData: FormData | null = null;
    public streamingText: string = "";

    public isAnalyzing: boolean = false;
    public hasSessionMessages: boolean = false;

    private streamingRaw: string = "";
    private converter: showdown.Converter;
    public sessionFiles: string[] = [];
    public selectedFiles: File[] = [];

    async ngOnInit() {
        const params = new URLSearchParams(window.location.search);
        this.sessionId = params.get("session_id") || "";

        this.converter = new showdown.Converter({
            tables: true, strikethrough: true, emoji: true,
            tasklists: true, simplifiedAutoLink: true,
            openLinksInNewWindow: true, ghCompatibleHeaderId: true,
            underline: true, backslashEscapesHTMLTags: false,
            literalMidWordUnderscores: true, parseImgDimensions: true,
            disableForced4SpacesIndentedSublists: true,
            literalMidWordAsterisks: true, noHeaderId: false,
            requireSpaceBeforeHeadingText: true, simpleLineBreaks: false,
        });

        await this.fetchSessionFiles();

        const styleElement = document.createElement("style");
        styleElement.innerHTML = new Markdown().css();
        document.head.appendChild(styleElement);

        await this.loadHistory();

        if (this.hasSessionMessages) return;

        const firstMessage = this.sessionFiles.length > 0
            ? this.sessionFiles.join(", ")
            : "(파일 분석 요청)";

        await wiz.call("save_message", {
            session_id: this.sessionId,
            role: "user",
            content: firstMessage
        });
        this.messages.push({ isMine: true, text: firstMessage });
        await this.service.render();

        const loadingMessage: ChatMessage = {
            isMine: false,
            isLoading: true,
            isLoadingSection1: true,   // section1 시작 전까지 로딩
            isLoadingSection2: true,   // section2 시작 전까지 로딩
            section1: "",
            section2: ""
        };
        this.messages.push(loadingMessage);

        const formData = new FormData();
        formData.append("session_id", this.sessionId);
        formData.append("function_name", "omission_generate");
        formData.append("query", firstMessage);

        this.currentFormData = formData;

        this.isStreaming = true;
        this.isAnalyzing = true;
        this.isAutoScrollEnabled = true;
        await this.service.render();

        // ✅ 초기 호출은 그대로 omission_generate
        this.initEventSource(formData, "omission_generate", loadingMessage);
    }

    private async fetchSessionFiles() {
        this.sessionFiles = [];
        if (!this.sessionId) return;

        const { code, data } = await wiz.call("get_session_files", { session_id: this.sessionId });
        if (code === 200 && Array.isArray(data?.file_names)) {
            this.sessionFiles = data.file_names;
        }
    }

    public parseMarkdown(text: string | null | undefined): string {
        if (!text) return "";
        return this.converter.makeHtml(text)
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, "&");
    }

    private extractSection(text: string, name: 'section1' | 'section2'): string {
        const open = `{${name}}`;
        const close = `{/${name}}`;
        const start = text.indexOf(open);
        if (start === -1) return "";
        const after = start + open.length;
        const end = text.indexOf(close, after);
        return end === -1 ? text.slice(after) : text.slice(after, end);
    }

    private splitSections(text: string): { section1: string; section2: string } {
        return {
            section1: this.extractSection(text, 'section1'),
            section2: this.extractSection(text, 'section2'),
        };
    }

    public async sendMessage() {
        let userMessage = (this.message || "").trim();

        if (!userMessage && this.selectedFiles.length > 0) {
            userMessage = this.selectedFiles.map(f => f.name).join(", ");
        }
        if (!userMessage && this.selectedFiles.length === 0) return;

        if (!this.sessionId) {
            const { code, data } = await wiz.call("create_session", { content: userMessage });
            if (code === 200) this.sessionId = data.session_id;
        }

        await wiz.call("save_message", {
            session_id: this.sessionId,
            role: "user",
            content: userMessage
        });

        const msgObj: ChatMessage = { text: userMessage, isMine: true };
        if (this.selectedFiles.length > 0) {
            msgObj.files = [...this.selectedFiles];
        }
        this.messages.push(msgObj);

        const functionName = this.hasSessionMessages ? "modify_generate" : "omission_generate";

        const loadingMessage: ChatMessage = {
            isMine: false,
            isLoading: true,
            isLoadingSection1: functionName === "omission_generate",
            isLoadingSection2: true,
            section1: "",
            section2: ""
        };
        this.messages.push(loadingMessage);

        const formData = new FormData();
        formData.append("query", userMessage);
        formData.append("session_id", this.sessionId);
        formData.append("function_name", functionName);
        for (const file of this.selectedFiles) formData.append("files[]", file);
        this.currentFormData = formData;

        this.message = "";
        this.selectedFiles = [];
        this.isStreaming = true;
        this.isAnalyzing = true;
        this.isAutoScrollEnabled = true;
        this.scrollToBottom(true);
        await this.service.render();

        this.initEventSource(formData, functionName, loadingMessage);
    }

    public handleFileSelect(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            for (const f of Array.from(input.files)) {
                this.selectedFiles.push(f);
            }
            input.value = "";
        }
    }

    public handleEnter(event: KeyboardEvent) {
        if (this.isStreaming || this.enterPressed) {
            event.preventDefault();
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            this.enterPressed = true;

            setTimeout(async () => {
                await this.sendMessage();
                this.enterPressed = false;
            }, 0);
        }
    }

    private scrollToBottom(force: boolean = false) {
        if (!this.isAutoScrollEnabled && !force) return;
    }

    private async initEventSource(formData: FormData, function_name: string, loadingMessage: ChatMessage) {
        const url = wiz.url('stream');
        this.streamingRaw = "";
        this.isAutoScrollEnabled = true;
        this.scrollToBottom(true);
        this.isStreaming = true;
        await this.service.render();

        try {
            formData.set("function_name", function_name);
            const response = await fetch(url, { method: "POST", body: formData });
            if (!response.body) throw new Error("No response body");

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");

            let isStreaming = true;
            let section1Started = false;
            let section2Started = false;

            while (isStreaming) {
                const { value, done } = await reader.read();
                if (done || !this.isStreaming) break;

                const chunk = decoder.decode(value, { stream: true });
                const events = chunk.split('\n');

                for (const event of events) {
                    if (!event.trim() || !event.startsWith("data:")) continue;
                    if (!this.isStreaming) return;

                    try {
                        const jsonData = JSON.parse(event.replace(/^data:/, "").trim());

                        if (jsonData.type === "finish") {
                            isStreaming = false;
                            this.isStreaming = false;

                            if (this.streamingRaw.trim()) {
                                await wiz.call("save_message", {
                                    session_id: this.sessionId,
                                    role: "llm",
                                    content: this.streamingRaw.trim()
                                });
                            }

                            loadingMessage.isLoading = false;
                            loadingMessage.isLoadingSection1 = false;
                            loadingMessage.isLoadingSection2 = false;

                            this.isAnalyzing = false;
                            if (function_name === "omission_generate") {
                                this.hasSessionMessages = true;
                                loadingMessage.showContinueButton = true;
                            }
                            await this.service.render();
                            break;
                        }

                        if (jsonData.type === "output") continue;

                        if (jsonData.data === "답변 생성 시작") {
                            this.streamingRaw = '';
                            loadingMessage.isSearching = false;
                        } else if (jsonData.data === "웹 검색 중") {
                            loadingMessage.isSearching = true;
                        } else {
                            loadingMessage.isSearching = false;
                            this.streamingRaw += jsonData.data;
                        }

                        const { section1, section2 } = this.splitSections(this.streamingRaw);
                        loadingMessage.section1 = section1;
                        loadingMessage.section2 = section2;

                        if (!section1Started && section1 && section1.trim().length > 0) {
                            section1Started = true;
                            loadingMessage.isLoadingSection1 = false;
                        }
                        if (!section2Started && section2 && section2.trim().length > 0) {
                            section2Started = true;
                            loadingMessage.isLoadingSection2 = false;
                        }

                        await this.service.render();
                        if (this.isAutoScrollEnabled) this.scrollToBottom();
                    } catch (parseError) {
                        console.error("Error parsing JSON stream data:", parseError, "Received data:", event);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching SSE data:", error);
        } finally {
            this.isStreaming = false;
            this.isAnalyzing = false;
            if (function_name === 'omission_generate') {
                loadingMessage.showContinueButton = true;
            }
            await this.service.render();
        }
    }

    private async loadHistory() {
        if (!this.sessionId) return;

        const { code, data } = await wiz.call("get_messages", { session_id: this.sessionId });
        const rows = (code === 200 && Array.isArray(data?.messages)) ? data.messages : [];

        this.hasSessionMessages = rows.length > 0;

        this.messages = [];
        for (const m of rows) {
            const role = m.role;
            const content = m.content || "";
            if (role === "user") {
                this.messages.push({ isMine: true, text: content });
            } else if (role === "llm") {
                const { section1, section2 } = this.splitSections(content);
                this.messages.push({
                    isMine: false,
                    section1, section2,
                    isLoading: false,
                    isLoadingSection1: false,
                    isLoadingSection2: false
                });
            }
        }
        await this.service.render();
    }

    public get latestRightMsg(): ChatMessage | null {
        for (let i = this.messages.length - 1; i >= 0; i--) {
            const m = this.messages[i];
            if (!m.isMine && m.section1 && m.section1.trim().length > 0) {
                return m;
            }
        }
        for (let i = this.messages.length - 1; i >= 0; i--) {
            const m = this.messages[i];
            if (!m.isMine && m.isLoadingSection1) {
                return m;
            }
        }
        return null;
    }
}

export default ComponentChatComponent;